import { useMemo } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

type TargetRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

interface OnboardingTooltipProps {
  open: boolean;
  title: string;
  description: string;
  currentStep: number;
  totalSteps: number;
  targetRect: TargetRect | null;
  isMobile: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}

const tooltipColor = "#4A42A8";

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function OnboardingTooltip({
  open,
  title,
  description,
  currentStep,
  totalSteps,
  targetRect,
  isMobile,
  onPrev,
  onNext,
  onClose,
}: OnboardingTooltipProps) {
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1280;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 720;
  const tooltipWidth = Math.min(360, viewportWidth - 32);
  const isLastStep = currentStep === totalSteps;

  const desktopPosition = useMemo(() => {
    if (!targetRect) {
      return null;
    }

    const gap = 18;
    const estimatedTooltipHeight = 220;
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetMidpointY = targetRect.top + targetRect.height / 2;
    const placeAbove = targetMidpointY >= viewportHeight * 0.6;
    const top = placeAbove
      ? clamp(targetRect.top - estimatedTooltipHeight - gap, 16, viewportHeight - estimatedTooltipHeight - 16)
      : clamp(targetRect.top + targetRect.height + gap, 16, viewportHeight - estimatedTooltipHeight - 16);
    const left = clamp(targetCenterX - tooltipWidth / 2, 16, viewportWidth - tooltipWidth - 16);
    const arrowLeft = clamp(targetCenterX - left - 8, 24, tooltipWidth - 24);

    return {
      top,
      left,
      arrowLeft,
      placeAbove,
    };
  }, [targetRect, tooltipWidth, viewportHeight, viewportWidth]);

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div className="pointer-events-none fixed inset-0 z-[120]">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0"
          >
            {targetRect ? (
              <>
                <div
                  className="absolute left-0 right-0 top-0 bg-black/60"
                  style={{ height: Math.max(targetRect.top - 8, 0) }}
                />
                <div
                  className="absolute left-0 bg-black/60"
                  style={{
                    top: Math.max(targetRect.top - 8, 0),
                    width: Math.max(targetRect.left - 8, 0),
                    height: targetRect.height + 16,
                  }}
                />
                <div
                  className="absolute right-0 bg-black/60"
                  style={{
                    top: Math.max(targetRect.top - 8, 0),
                    left: targetRect.left + targetRect.width + 8,
                    height: targetRect.height + 16,
                  }}
                />
                <div
                  className="absolute bottom-0 left-0 right-0 bg-black/60"
                  style={{ top: targetRect.top + targetRect.height + 8 }}
                />
                <div
                  className="absolute rounded-2xl border-2 border-white/90 shadow-[0_0_0_1px_rgba(255,255,255,0.15)]"
                  style={{
                    top: targetRect.top - 8,
                    left: targetRect.left - 8,
                    width: targetRect.width + 16,
                    height: targetRect.height + 16,
                  }}
                />
              </>
            ) : (
              <div className="absolute inset-0 bg-black/60" />
            )}
          </motion.div>

          {isMobile ? (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              className="pointer-events-auto fixed inset-x-0 bottom-0 max-h-[40vh] overflow-hidden rounded-t-[28px] px-4 pb-4 pt-4 shadow-2xl"
              style={{ backgroundColor: tooltipColor }}
            >
              <div className="mx-auto mb-4 h-1.5 w-16 rounded-full bg-white/25" />
              <div className="flex max-h-[calc(40vh-2rem)] flex-col">
                <div className="flex items-center justify-between text-sm text-white/80">
                  <span>{currentStep}/{totalSteps}</span>
                  <button className="font-medium text-white" onClick={onClose}>
                    Fechar
                  </button>
                </div>
                <div className="mt-3 flex-1 overflow-y-auto pr-1">
                  <h3 className="text-lg font-semibold text-white">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-white/90">{description}</p>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <button
                    className="inline-flex h-11 flex-1 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-sm font-medium text-white disabled:opacity-50"
                    onClick={onPrev}
                    disabled={currentStep === 1}
                  >
                    Anterior
                  </button>
                  <button
                    className="inline-flex h-11 flex-1 items-center justify-center rounded-xl bg-white text-sm font-semibold"
                    style={{ color: tooltipColor }}
                    onClick={onNext}
                  >
                    {isLastStep ? "Concluir" : "Próximo"}
                  </button>
                </div>
              </div>
            </motion.div>
          ) : desktopPosition ? (
            <div
              className="pointer-events-auto fixed"
              style={{
                left: desktopPosition.left,
                top: desktopPosition.top,
                width: tooltipWidth,
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: desktopPosition.placeAbove ? 8 : -8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: desktopPosition.placeAbove ? 8 : -8, scale: 0.98 }}
                className="relative w-[min(360px,calc(100vw-32px))] rounded-2xl px-5 py-4 shadow-2xl"
                style={{
                  backgroundColor: tooltipColor,
                  width: tooltipWidth,
                }}
              >
                <div
                  className="absolute h-4 w-4 rotate-45"
                  style={{
                    backgroundColor: tooltipColor,
                    left: desktopPosition.arrowLeft,
                    top: desktopPosition.placeAbove ? "auto" : -6,
                    bottom: desktopPosition.placeAbove ? -6 : "auto",
                  }}
                />
                <div className="relative space-y-3">
                  <div className="flex items-center justify-between text-sm text-white/80">
                    <span>{currentStep}/{totalSteps}</span>
                    <button className="font-medium text-white" onClick={onClose}>
                      Fechar
                    </button>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white">{title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-6 text-white/90">{description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="inline-flex h-10 flex-1 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-sm font-medium text-white disabled:opacity-50"
                      onClick={onPrev}
                      disabled={currentStep === 1}
                    >
                      Anterior
                    </button>
                    <button
                      className="inline-flex h-10 flex-1 items-center justify-center rounded-xl bg-white text-sm font-semibold"
                      style={{ color: tooltipColor }}
                      onClick={onNext}
                    >
                      {isLastStep ? "Concluir" : "Próximo"}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          ) : null}
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
